/**
 * Atelier Mino — micro-animations des fiches produit (06/10/2026), chargées par le bloc mino-gravure (réglages « Animation
 * aiguille », « Animation laser »). Décor seulement : rien ne les attend, chaque étape passe par setTimeout (requestAnimationFrame
 * et les animations sont suspendus quand le panneau du navigateur de test est masqué). Mouvement réduit : rien.
 */
(() => {
  if (window.MinoAnimations) return;
  const reduit = matchMedia('(prefers-reduced-motion: reduce)');
  const SORTIE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const borne = (v, a, b) => Math.min(b, Math.max(a, v));
  const f1 = (v) => v.toFixed(1);
  const animer = (n, k, o) => (n && n.animate ? n.animate(k, o) : null);
  // feuille chargée sans bloquer l'affichage : rien n'est animé avant qu'elle s'applique
  const pret = () => !reduit.matches && getComputedStyle(document.documentElement).getPropertyValue('--mino-animations').trim() === '1';

  /* ---------- Aiguille : au changement de pastille, couture du tour de l'image visible, fil couleur de la pastille.
   * Image prête : 0,52 s de couture au moins puis 0,3 s d'effacement ; sinon couture jusqu'au chargement, 2,5 s au plus. */
  const TOUR = 3300, MINI = 520, MAXI = 2500, EFFACE = 300;   // couture ralentie à la demande de Thomas (06/10) : ≈ 0,9 s image en cache

  /** Contour arrondi en retrait du cadre (bas relevé au-dessus des points du diaporama), sens horaire depuis le haut gauche. */
  function contour(w, h, bas = 0) {
    const m = Math.min(w, h), i = borne(m * 0.045, 12, 40), r = borne(m * 0.05, 8, 40);
    const x0 = i, y0 = i, x1 = w - i, y1 = h - Math.max(i, bas);
    const a = Math.max(0, x1 - x0 - 2 * r), b = Math.max(0, y1 - y0 - 2 * r), q = (Math.PI * r) / 2, P = Math.PI / 2;
    const segs = [{ l: a, x: x0 + r, y: y0, d: 0 }, { l: q, cx: x1 - r, cy: y0 + r, t: -P }, { l: b, x: x1, y: y0 + r, d: 90 },
      { l: q, cx: x1 - r, cy: y1 - r, t: 0 }, { l: a, x: x1 - r, y: y1, d: 180 }, { l: q, cx: x0 + r, cy: y1 - r, t: P },
      { l: b, x: x0, y: y1 - r, d: 270 }, { l: q, cx: x0 + r, cy: y0 + r, t: 2 * P }];
    let s = 0;
    segs.forEach((g) => { g.s = s; s += g.l; });
    return { segs, L: s, r, m };
  }

  /** Point et direction (degrés, déroulée d'un tour à l'autre) à la position p, en tours. */
  function point(c, p) {
    const k = Math.floor(p), s = (p - k) * c.L;
    const g = c.segs.find((x) => s <= x.s + x.l) || c.segs[7];
    const u = g.l ? (s - g.s) / g.l : 0;
    if (g.cx !== undefined) {
      const t = g.t + (u * Math.PI) / 2;
      return { x: g.cx + c.r * Math.cos(t), y: g.cy + c.r * Math.sin(t), d: (t * 180) / Math.PI + 90 + 360 * k };
    }
    const t = (g.d * Math.PI) / 180;
    return { x: g.x + Math.cos(t) * u * g.l, y: g.y + Math.sin(t) * u * g.l, d: g.d + 360 * k };
  }

  /** Tracé SVG du contour de p0 à p1 (en tours). */
  function trace(c, p0, p1) {
    const a = point(c, p0), s0 = p0 * c.L, s1 = p1 * c.L;
    let d = `M${f1(a.x)} ${f1(a.y)}`;
    for (let k = Math.floor(p0); k * c.L < s1; k++) {
      for (const g of c.segs) {
        const debut = k * c.L + g.s, fin = debut + g.l;
        if (!g.l || fin <= s0) continue;
        if (debut >= s1) return d;
        const b = point(c, Math.min(fin, s1) / c.L);
        d += g.cx !== undefined ? `A${f1(c.r)} ${f1(c.r)} 0 0 1 ${f1(b.x)} ${f1(b.y)}` : `L${f1(b.x)} ${f1(b.y)}`;
      }
    }
    return d;
  }

  /** Images clés de l'aiguille : translation et rotation, animées par le compositeur (fluides même si la page travaille). */
  function cles(c, p0, duree) {
    const p1 = p0 + duree / TOUR, ps = [p0, p1];
    for (let k = Math.floor(p0); k < p1; k++) {
      c.segs.forEach((g) => {
        const n = g.cx !== undefined ? 6 : 1;
        for (let j = 0; j < n; j++) { const p = k + (g.s + (g.l * j) / n) / c.L; if (p > p0 && p < p1) ps.push(p); }
      });
    }
    return ps.sort((x, y) => x - y).map((p) => {
      const q = point(c, p);
      return { offset: (p - p0) / (p1 - p0), transform: `translate(${f1(q.x)}px,${f1(q.y)}px) rotate(${f1(q.d)}deg)` };
    });
  }

  /** Aiguille en aplats (pas de dégradé), pointe en (0, 0) vers la droite, brin de fil au chas. */
  function aiguille(lg, ep) {
    const e = lg * 0.08, w = lg * 1.6, h = e * 8, v = (x) => f1(x * lg);
    const brin = `M${v(-0.865)} 0C${v(-1.05)} 0 ${v(-1.15)} ${f1(e * 1.9)} ${v(-1.52)} ${f1(e * 1.7)}`;
    return `<svg class="mino-aiguille__corps" viewBox="${v(-1.55)} ${f1(-h / 2)} ${f1(w)} ${f1(h)}" width="${f1(w)}" height="${f1(h)}" style="left:${v(-1.55)}px;top:${f1(-h / 2)}px" focusable="false">`
      + `<path class="mino-aiguille__dessous" d="${brin}" stroke-width="${f1(ep + 1.4)}"/><path class="mino-aiguille__fil" d="${brin}" stroke-width="${f1(ep)}"/>`
      + `<path class="mino-aiguille__metal" d="M0 0L${v(-0.22)} ${f1(-e / 2)}L${f1(-lg + e / 2)} ${f1(-e / 2)}A${f1(e / 2)} ${f1(e / 2)} 0 0 0 ${f1(-lg + e / 2)} ${f1(e / 2)}L${v(-0.22)} ${f1(e / 2)}Z"/>`
      + `<path class="mino-aiguille__reflet" d="M${v(-0.3)} ${f1(-e * 0.2)}L${v(-0.76)} ${f1(-e * 0.2)}" stroke-width="${f1(e * 0.26)}"/>`
      + `<rect class="mino-aiguille__chas" x="${v(-0.93)}" y="${f1(-e * 0.19)}" width="${v(0.13)}" height="${f1(e * 0.38)}" rx="${f1(e * 0.19)}"/></svg>`;
  }

  /** Glisse l'ancienne photo (et son prénom) sous la nouvelle image du cadre ; placée après l'image, que mino-apercu vise
   *  toujours en premier. Jamais une image encore affichée ailleurs. */
  function poserDessous(cadre, ancien, sous) {
    if (!sous) {
      const img = ancien?.querySelector('img'), prenom = ancien?.querySelector(':scope > .mino-surimpression');
      if (!img || ancien.isConnected) return null;
      const cs = getComputedStyle(cadre.querySelector('img') || img);
      sous = document.createElement('div');
      sous.className = 'mino-anim-avant';
      sous.setAttribute('aria-hidden', 'true');
      img.className = '';
      img.alt = '';
      img.style.objectFit = cs.objectFit;
      img.style.objectPosition = cs.objectPosition;
      sous.append(img);
      if (prenom) sous.append(prenom);
    }
    cadre.appendChild(sous);
    return sous;
  }

  /** Une couture, du clic à l'image prête (2,5 s au plus), puis effacement. */
  class Couture {
    constructor(h, cadre, fil, p0, suite) {
      Object.assign(this, { h, p0, t0: performance.now(), galerie: h.galerie(), mediaAvant: cadre.dataset.mediaId });
      this.n = document.createElement('div');
      this.n.className = 'mino-aiguille';
      this.n.setAttribute('aria-hidden', 'true');
      this.n.style.setProperty('--mino-fil', fil);
      this.minuteries = [setTimeout(() => this.echeance(), MAXI), setInterval(() => this.verifier(), 100)];
      this.attacher(cadre, !suite);
    }

    ecoule() { return performance.now() - this.t0; }
    /** Position en tours, au temps de l'aiguille : la couture ne la devance jamais. */
    position() { const t = this.marche?.currentTime; return this.p0 + (typeof t === 'number' ? t : this.ecoule()) / TOUR; }

    /** Pose la couture dans un cadre (au clic, puis dans la galerie remplacée) là où elle en est. */
    attacher(cadre, entree) {
      this.cadre = cadre;
      cadre.classList.add('mino-anim-hote');
      const w = cadre.clientWidth, h = cadre.clientHeight, c = this.h.contour(cadre);
      if (!this.c || this.c.L !== c.L || this.taille !== `${w}x${h}`) {     // même cadre ailleurs : on déplace sans reconstruire
        const m = c.m, lg = borne(m * 0.06, 28, 52), ep = borne(m * 0.0028, 1.7, 2.6), tiret = borne(m * 0.009, 5, 8);
        const pts = `stroke-dasharray="${f1(tiret)} ${f1(tiret * 0.75)}"`;
        this.n.innerHTML = `<svg class="mino-aiguille__couture" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" focusable="false">`
          + `<path class="mino-aiguille__dessous" stroke-width="${f1(ep + 1.6)}" ${pts}/><path class="mino-aiguille__fil" stroke-width="${f1(ep)}" ${pts}/></svg>`
          + `<div class="mino-aiguille__pointe"><div class="mino-aiguille__geste">${aiguille(lg, ep)}</div></div>`;
      }
      this.c = c;
      this.taille = `${w}x${h}`;
      cadre.appendChild(this.n);
      this.chemins = this.n.querySelectorAll('.mino-aiguille__couture path');
      this.geste = this.n.querySelector('.mino-aiguille__geste');
      const t = this.marche?.currentTime;                  // temps de l'aiguille : elle repart exactement d'où elle est
      this.marche?.cancel();
      this.marche = animer(this.geste.parentNode, cles(this.c, this.p0, MAXI + 400), { duration: MAXI + 400, fill: 'forwards' });
      if (this.marche) this.marche.currentTime = typeof t === 'number' ? t : 0;
      if (entree) animer(this.geste, [{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'none' }], { duration: 160, easing: SORTIE });
      this.dessiner();
    }

    /** Points de couture derrière l'aiguille, un tour au plus. */
    dessiner() {
      cancelAnimationFrame(this.raf);
      if (this.fini) return;
      const fin = Math.min(this.position(), this.p0 + 0.999);
      const d = fin > this.p0 ? trace(this.c, this.p0, fin) : '';
      this.chemins.forEach((c) => c.setAttribute('d', d));
      this.raf = requestAnimationFrame(() => this.dessiner());
    }

    /** Suit la galerie remplacée et guette l'image prête (observateur, chargement d'image, toutes les 100 ms). */
    verifier() {
      if (this.fini) return;
      const g = this.h.galerie();
      if (!g || g === this.galerie) return;                 // réponse d'Horizon en attente : l'ancienne image reste
      if (!this.n.isConnected) {                            // galerie remplacée : l'aiguille passe dans la nouvelle (pas de suivi des glissements)
        const cadre = this.h.cadreVisible();
        if (!cadre) return;
        const ancien = this.cadre;
        this.attacher(cadre, false);
        if (cadre.dataset.mediaId !== this.mediaAvant) this.dessous(cadre, ancien);
      }
      const img = this.cadre.querySelector('img');
      if (this.cadre.classList.contains('mino-attente') || !img?.complete || !img.naturalWidth) { this.prete = 0; return; }
      // prête : confirmée 75 ms plus tard (mino-apercu pose l'image sans prénom en 30 ms)
      if (!this.prete) {
        this.prete = performance.now();
        this.minuteries.push(setTimeout(() => this.verifier(), Math.max(80, MINI - this.ecoule())));
      } else if (performance.now() - this.prete >= 75 && this.ecoule() >= MINI) this.finir();
    }

    /** L'ancienne photo (et son prénom) reste sous la nouvelle jusqu'à la fin de la couture : l'aiguille coud autour d'elle
     *  pendant le chargement, puis la nouvelle arrive en fondu enchaîné (celui de mino-apercu, ou le nôtre si elle était en
     *  cache). Clics rapides : le dessous passe d'une couture à l'autre. */
    dessous(cadre, ancien) {
      this.sous = poserDessous(cadre, ancien, this.sous);
      const img = cadre.querySelector('img');
      if (!cadre.classList.contains('mino-attente') && img?.complete && !this.h.prenom()) {
        cadre.classList.add('mino-anim-fondu');
        setTimeout(() => cadre.classList.remove('mino-anim-fondu'), 400);
      }
    }

    /** 2,5 s : l'image est montrée de toute façon, et celle de la galerie suivante si Horizon répond plus tard. */
    echeance() {
      if (this.fini) return;
      if (this.h.galerie() === this.galerie) {             // Horizon n'a pas encore répondu : on garde de quoi glisser l'ancienne photo
        this.h.forcerApres = { galerie: this.galerie, cadre: this.cadre, sous: this.sous };
        this.sous = null;
      } else this.h.montrer(this.h.cadreVisible());
      this.finir();
    }

    finir() {
      if (this.fini) return;
      this.fini = true;
      this.minuteries.forEach(clearTimeout);
      cancelAnimationFrame(this.raf);
      animer(this.geste, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(1.25)' }], { duration: 200, easing: SORTIE, fill: 'forwards' });
      animer(this.n, [{ opacity: 1 }, { opacity: 0 }], { duration: EFFACE, easing: 'ease-out', fill: 'forwards' });
      setTimeout(() => this.retirer(), EFFACE + 40);
    }

    /** Arrêt net (clic suivant) ou fin de l'effacement. */
    retirer() {
      this.fini = true;
      this.minuteries.forEach(clearTimeout);
      cancelAnimationFrame(this.raf);
      this.n.getAnimations?.({ subtree: true }).forEach((a) => a.cancel());
      this.n.remove();
      this.sous?.remove();
      if (this.h.couture === this) this.h.couture = null;
    }
  }

  class Aiguille {
    constructor(racine, option) {
      this.racine = racine;
      this.section = racine.closest('.shopify-section') || document.body;
      this.section.addEventListener('change', (e) => {
        const r = e.target;
        if (r instanceof HTMLInputElement && r.type === 'radio' && r.dataset.optionName === option && r.closest('variant-picker')) this.demarrer(r);
      });
      // préchargement 350 ms après le premier survol ou contact : si un clic suit, il attend la réponse d'Horizon
      const survol = (e) => {
        if (this.precharge || !(e.target instanceof Element) || !e.target.closest('variant-picker')) return;
        this.precharge = true;
        setTimeout(() => this.precharger(), 350);
      };
      ['pointerover', 'touchstart', 'focusin'].forEach((t) => this.section.addEventListener(t, survol, { passive: true }));
      this.section.addEventListener('load', (e) => { if (e.target instanceof HTMLImageElement) this.couture?.verifier(); }, true);
      new MutationObserver(() => this.observer()).observe(this.section, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    galerie() { return this.section.querySelector('media-gallery'); }
    /** Prénom affiché sur la photo par mino-apercu (texte saisi, ou position choisie) : il change lui-même l'image. */
    prenom() { const a = this.racine.minoApercu; return Boolean(a && (a.texte() || a.position())); }

    /** Image visible : 1re de la grille (ordinateur) ou diapositive affichée (mobile), hors loupe. */
    cadreVisible() {
      for (const c of this.galerie()?.querySelectorAll('.product-media[data-media-id]') || []) {
        if (c.closest('dialog') || c.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'true') continue;
        if (c.getBoundingClientRect().width > 0) return c;
      }
      return null;
    }

    /** Contour dans ce cadre, relevé au-dessus des points du diaporama (mobile) et du bas de l'écran (image plus haute que la fenêtre). */
    contour(cadre) {
      const rc = cadre.getBoundingClientRect();
      const pts = [...(cadre.closest('media-gallery')?.querySelectorAll('slideshow-controls') || [])]
        .map((e) => e.getBoundingClientRect()).find((r) => r.height && r.top < rc.bottom && r.bottom > rc.top);
      const bas = Math.max(pts ? rc.bottom - pts.top + 4 : 0, rc.bottom - innerHeight + 16);
      return contour(cadre.clientWidth, cadre.clientHeight, Math.min(bas, cadre.clientHeight * 0.45));
    }

    /** Départ en haut à gauche ; caché (en-tête collant sur mobile) : en bas à droite, près des pastilles. */
    depart(cadre) {
      const c = this.contour(cadre), r = cadre.getBoundingClientRect(), g = cadre.closest('media-gallery');
      const vu = (p) => { const q = point(c, p), e = document.elementFromPoint(r.left + q.x, r.top + q.y); return Boolean(e && g?.contains(e)); };
      const bas = c.segs[4].s / c.L;
      return vu(0) || !vu(bas) ? 0 : bas;
    }

    demarrer(radio) {
      const reste = this.forcerApres?.sous;                 // photo d'avant encore sous l'image (réponse d'Horizon en retard)
      this.forcerApres = null;
      this.section.querySelectorAll('.mino-anim-montre').forEach((c) => c.classList.remove('mino-anim-montre'));
      const cadre = pret() && this.cadreVisible();
      if (!cadre) return;
      const t = radio.closest('label')?.querySelector('.variant-option__button-label__text');
      const fil = (t && getComputedStyle(t).getPropertyValue('--mino-pastille').trim()) || '#3B2340';
      const avant = this.couture, suite = Boolean(avant && !avant.fini);
      const p0 = suite ? avant.position() % 1 : this.depart(cadre);
      const sous = suite ? avant.sous : reste || null;      // photo d'avant gardée pour la couture suivante
      if (avant) { avant.sous = null; avant.retirer(); }
      this.couture = new Couture(this, cadre, fil, p0, suite);
      this.couture.sous = sous;
    }

    montrer(cadre) {
      if (!cadre) return;
      cadre.classList.add('mino-anim-montre');
      this.montre = true;
    }

    observer() {
      this.couture?.verifier();
      const f = this.forcerApres;
      if (f && this.galerie() !== f.galerie) {
        // Horizon a répondu après 2,5 s : l'ancienne photo reste dessous jusqu'au chargement de la nouvelle, montrée sans
        // attendre (après que mino-apercu y a posé l'image sans prénom, 30 ms)
        this.forcerApres = null;
        const cadre = this.cadreVisible();
        const sous = cadre && cadre.dataset.mediaId !== f.cadre.dataset.mediaId ? poserDessous(cadre, f.cadre, f.sous) : null;
        if (sous) {
          const img = cadre.querySelector('img'), fin = () => sous.remove();
          img?.addEventListener('load', () => setTimeout(fin, 350), { once: true });
          setTimeout(fin, 8000);
          if (img?.complete) setTimeout(fin, 400);
        }
        setTimeout(() => this.montrer(cadre), 120);
      }
      if (this.montre) {                                      // image révélée par mino-apercu : forçage inutile
        this.section.querySelectorAll('.mino-anim-montre:not(.mino-attente)').forEach((c) => c.classList.remove('mino-anim-montre'));
        this.montre = Boolean(this.section.querySelector('.mino-anim-montre'));
      }
    }

    /** Images des variantes aux largeurs de l'image visible (même cache), une à une, en basse priorité, jamais tant qu'une
     *  image choisie se charge (sur réseau lent, elles prendraient sa bande passante à Horizon et à l'image). */
    precharger() {
      if (navigator.connection?.saveData) return;
      if (this.couture && !this.couture.fini) return setTimeout(() => this.precharger(), 250);
      if (this.prenom()) return this.racine.minoApercu.precharger();          // prénom affiché : images sans prénom (mino-apercu)
      const ref = this.cadreVisible()?.querySelector('img');
      if (!ref) return;
      const images = JSON.parse(this.racine.querySelector('[data-mino-anim-images]')?.textContent || '{}');
      const largeurs = (ref.getAttribute('srcset') || '').split(',').map((x) => parseInt(x.trim().split(/\s+/)[1], 10)).filter(Boolean);
      const file = Object.values(images);
      const suivante = () => {
        const base = file.shift();
        if (!base) return;
        if (this.couture && !this.couture.fini) { file.unshift(base); return setTimeout(suivante, 250); }
        const im = new Image();
        im.fetchPriority = 'low';
        im.onload = im.onerror = suivante;
        if (ref.sizes) im.sizes = ref.sizes;
        if (largeurs.length) im.srcset = largeurs.map((l) => `${base}${base.includes('?') ? '&' : '?'}width=${l} ${l}w`).join(', ');
        else im.src = base;
      };
      suivante();
    }
  }

  /* ---------- Laser : point au bout du texte à chaque lettre ajoutée (calque photo, copie mobile, dessin SVG), éteint 500 ms
   * après la dernière frappe ; police ou position changée : balayage du nom. Position lue sur le rendu (getScreenCTM). Sur la
   * fiche, remplace le flou d'apparition du dessin (mino-objet, partagé avec l'accueil, non modifié). */
  const SCINTILLE = 150, EXTINCTION = 500, FUMEE = 700, BALAYAGE = 600;
  const mesure = document.createElement('canvas').getContext('2d');
  const WISP = 'd="M6 31.5C3 27 9 23.5 6 18.5S3 10 7 6.5 6.5 1.5 4.5 0.5"';
  const VOLUTE = `<svg class="mino-laser__fumee" viewBox="0 0 12 32" preserveAspectRatio="none" focusable="false"><path class="mino-laser__voile" ${WISP}/><path class="mino-laser__brin" ${WISP}/></svg>`;

  /** Début et bout du texte (encre de la 1re et de la dernière lettre, à mi-hauteur des minuscules), en px dans l'hôte. */
  function geometrie(t, hote) {
    const n = t.getNumberOfChars(), m = n && t.getScreenCTM();
    const cs = getComputedStyle(t), fs = parseFloat(t.getAttribute('font-size')) || parseFloat(cs.fontSize);
    if (!m || !fs) return null;
    const texte = cs.textTransform === 'uppercase' ? t.textContent.toLocaleUpperCase('fr') : t.textContent;
    const maj = texte === texte.toLocaleUpperCase('fr') && texte !== texte.toLocaleLowerCase('fr'), car = [...texte], k = fs / 100;
    mesure.font = `${cs.fontWeight} 100px ${cs.fontFamily}`;
    const pre = mesure.measureText(car[0]), der = mesure.measureText(car[car.length - 1]);
    const dy = cs.dominantBaseline === 'middle' ? 0 : (-mesure.measureText(maj ? 'H' : 'x').actualBoundingBoxAscent * k) / 2;
    const a = t.getStartPositionOfChar(0), b = t.getEndPositionOfChar(n - 1), r = hote.getBoundingClientRect();
    const ecran = (x, y) => ({ x: m.a * x + m.c * y + m.e - r.left, y: m.b * x + m.d * y + m.f - r.top });
    return {
      debut: ecran(a.x - pre.actualBoundingBoxLeft * k, a.y + dy),
      fin: ecran(b.x - (parseFloat(cs.letterSpacing) || 0) - (der.width - der.actualBoundingBoxRight) * k, b.y + dy),
      fpx: fs * Math.hypot(m.a, m.b),
    };
  }

  class Laser {
    constructor(racine) {
      this.section = racine.closest('.shopify-section') || document.body;
      // tous les champs de gravure (lot de 2 : deux champs, 12/10/2026) ; valeur précédente de chacun
      this.valeurs = new Map([...racine.querySelectorAll('[data-mino-champ]')].map((c) => [c, c.value.trim()]));
      this.hotes = new Set();
      this.section.classList.add('mino-laser-actif');
      // capture : avant mino-gravure.js, qui regrave le dessin dès l'événement
      racine.addEventListener('input', (e) => { if (this.valeurs.has(e.target)) this.saisie(e); }, true);
      racine.addEventListener('change', (e) => {
        if (!e.target.matches?.('[data-mino-police], [data-mino-position]') || !pret()) return;
        this.frappe = null;                                   // le balayage l'emporte sur le point de la dernière frappe
        this.regravure = { t: performance.now(), vus: new WeakSet() };
      }, true);
      new MutationObserver((r) => this.rendu(r)).observe(this.section, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['x', 'y', 'font-size'] });
    }

    saisie(e) {
      // ajout = texte plus long (sur Android, effacer pendant une composition arrive en « insertCompositionText ») ;
      // même longueur : lettre remplacée par une autre frappe
      const avant = this.valeurs.get(e.target) || '';
      const valeur = e.target.value.trim(), n0 = [...avant].length, n1 = [...valeur].length;
      const ajout = n1 > n0 || (n1 === n0 && valeur !== avant && /^insert/.test(e.inputType || ''));
      this.valeurs.set(e.target, valeur);
      this.regravure = null;
      this.frappe = ajout && valeur && pret() ? { t: performance.now(), vus: new WeakSet() } : null;
      if (!this.frappe) this.hotes.forEach((o) => (o.n.isConnected ? this.eteindre(o) : this.hotes.delete(o)));
    }

    /** Texte redessiné par mino-apercu.js (calques) ou mino-objet.js (dessin) : point ou balayage. */
    rendu(recs) {
      const now = performance.now();
      const frappe = this.frappe && now - this.frappe.t < 600 ? this.frappe : null;
      const regravure = this.regravure && now - this.regravure.t < 1500 ? this.regravure : null;
      if (!frappe && !regravure) return;
      const textes = new Set();
      recs.forEach((r) => {
        const t = (r.target.nodeType === 1 ? r.target : r.target.parentElement)?.closest('.mino-surimpression > text, text[data-mino-grave]');
        if (t && !t.closest('.mino-anim-avant')) textes.add(t);
        r.addedNodes.forEach((a) => a.nodeType === 1 && a.matches('.mino-surimpression') && a.firstElementChild && textes.add(a.firstElementChild));
      });
      textes.forEach((t) => {
        if (!t.textContent.trim() || !t.getBoundingClientRect().width) return;   // vide ou masqué (copie mobile sur ordinateur)
        try {
          const o = this.calque(t), g = o && geometrie(t, o.hote);
          if (!g) return;
          if (frappe) { this.poser(o, g, !frappe.vus.has(t), frappe.t); frappe.vus.add(t); }
          else if (!regravure.vus.has(t)) { regravure.vus.add(t); this.balayer(o, g); }
        } catch (err) { /* décor : jamais d'erreur pour la cliente */ }
      });
    }

    /** Calque du laser dans l'hôte du texte (cadre de la photo, copie mobile, dessin), créé une fois puis réutilisé. */
    calque(t) {
      const hote = t.closest('.product-media, .mino-gravure__apercu-cadre, [data-mino-apercu-produit]');
      if (!hote || hote.closest('dialog')) return null;
      if (hote.minoLaser?.n.parentNode === hote) return hote.minoLaser;
      this.hotes.forEach((x) => x.n.isConnected || this.hotes.delete(x));   // calques des galeries remplacées : oubliés
      hote.classList.add('mino-anim-hote');
      const n = document.createElement('div');
      n.className = 'mino-laser';
      n.setAttribute('aria-hidden', 'true');
      n.innerHTML = `<div class="mino-laser__pos"><div class="mino-laser__point"></div></div>${VOLUTE.repeat(3)}`;
      hote.appendChild(n);
      const o = { hote, n, pos: n.firstChild, point: n.firstChild.firstChild, fumees: [...n.querySelectorAll('.mino-laser__fumee')], i: 0, minuteries: [] };
      this.hotes.add(o);
      return (hote.minoLaser = o);
    }

    /** Arrête ce qui reste d'un point ou d'un balayage (minuteries, volutes à venir, scintillement). */
    couper(o) {
      o.minuteries.forEach(clearTimeout);
      o.minuteries = [];
      o.balayage?.cancel();
      o.scintille?.cancel();
    }

    allumer(o, g) {
      this.couper(o);
      o.n.style.setProperty('--mino-laser', `${f1(borne(g.fpx * 0.075, 1.8, 8))}px`);
      o.n.classList.add('is-allume');
    }

    /** Point au bout du texte ; nouvelle lettre : scintillement et volute ; éteint 500 ms après la dernière frappe. */
    poser(o, g, nouvelle, t0) {
      this.allumer(o, g);
      o.n.classList.remove('is-balayage');
      o.pos.style.transform = `translate(${f1(g.fin.x)}px,${f1(g.fin.y)}px)`;
      if (nouvelle) {
        o.scintille = animer(o.point, [1.35, 0.9, 1.15, 0.95, 1].map((s, i) => ({ opacity: [1, 0.55, 1, 0.75, 1][i], transform: `scale(${s})` })), { duration: SCINTILLE });
        this.fumee(o, g.fin.x, g.fin.y, g.fpx);
      }
      // compté depuis la frappe (mino-apercu redessine 30 ms plus tard) ; fondu de 0,1 s : éteint à 500 ms
      o.minuteries.push(setTimeout(() => this.eteindre(o), Math.max(0, EXTINCTION - 120 - (performance.now() - t0))));
    }

    /** Balayage du nom de gauche à droite, comme si on le regravait. */
    balayer(o, g) {
      this.allumer(o, g);
      o.n.classList.add('is-balayage');
      const angle = (Math.atan2(g.fin.y - g.debut.y, g.fin.x - g.debut.x) * 180) / Math.PI;
      const en = (p) => `translate(${f1(p.x)}px,${f1(p.y)}px) rotate(${f1(angle)}deg)`;
      o.pos.style.transform = en(g.debut);
      o.balayage = animer(o.pos, [{ transform: en(g.debut) }, { transform: en(g.fin) }], { duration: BALAYAGE, fill: 'forwards' });   // vitesse régulière, comme une tête laser
      o.scintille = animer(o.point, [{ opacity: 1 }, { opacity: 0.7 }, { opacity: 1 }], { duration: SCINTILLE, iterations: BALAYAGE / SCINTILLE });
      [0.15, 0.5, 0.85].forEach((f) => o.minuteries.push(setTimeout(() => this.fumee(o, g.debut.x + (g.fin.x - g.debut.x) * f, g.debut.y + (g.fin.y - g.debut.y) * f, g.fpx), BALAYAGE * f)));
      o.minuteries.push(setTimeout(() => {
        o.balayage?.cancel();
        o.pos.style.transform = en(g.fin);
        this.eteindre(o);
      }, BALAYAGE));
    }

    /** Volute qui monte et se dissipe (3 par calque, réutilisées). */
    fumee(o, x, y, fpx) {
      const f = o.fumees[o.i++ % 3], h = borne(fpx * 0.75, 12, 34), dx = h * (o.i % 2 ? 0.14 : -0.12);
      f.style.cssText = `width:${f1(h * 0.4)}px;height:${f1(h)}px;margin:${f1(-h)}px 0 0 ${f1(-h * 0.2)}px`;
      f.minoAnim?.cancel();
      f.minoAnim = animer(f, [
        { opacity: 0, transform: `translate(${f1(x)}px,${f1(y)}px) scale(0.5)` },
        { opacity: 0.8, transform: `translate(${f1(x + dx)}px,${f1(y - h * 0.25)}px) scale(0.85)`, offset: 0.3 },
        { opacity: 0, transform: `translate(${f1(x - dx)}px,${f1(y - h * 0.8)}px) scale(1.25)` },
      ], { duration: FUMEE, easing: SORTIE });
    }

    eteindre(o) {
      this.couper(o);
      o.n.classList.remove('is-allume', 'is-balayage');
    }
  }

  function demarrer() {
    document.querySelectorAll('mino-gravure[data-anim-aiguille], mino-gravure[data-anim-laser]').forEach((el) => {
      if (el.minoAnimations) return;
      el.minoAnimations = true;
      if (el.dataset.animAiguille) new Aiguille(el, el.dataset.animAiguille);
      if (el.hasAttribute('data-anim-laser') && !reduit.matches) new Laser(el);
    });
  }
  window.MinoAnimations = { demarrer };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
  document.addEventListener('shopify:section:load', demarrer);   // éditeur de thème : section re-rendue
})();
